// ABOUTME: Hook for recording a voice note and transcribing it server-side.
// Records with MediaRecorder, uploads the clip to POST /agent/voice-notes
// (ElevenLabs speech-to-text), and hands back the transcript plus an
// AttachmentRef so the composer can post it like any other attached message.
// Replaces the browser-native Web Speech API dictation this repo used before
// (#2609) — one reliable behavior across Safari/mobile webviews, where the
// Web Speech API is unreliable or absent, instead of two live voice paths.

import { useCallback, useRef, useState } from 'react'
import { fetchWithAuthRetry, getBaseUrl } from '@/api/client'
import { useAuthStore } from '@/stores/useAuthStore'
import type { AttachmentRef } from '@/types'

export type VoiceRecorderStatus =
  | 'idle'
  | 'requesting_permission'
  | 'recording'
  | 'transcribing'
  | 'error'
  | 'unsupported'

export interface VoiceRecorderError {
  type: 'not-allowed' | 'no-speech' | 'unsupported' | 'network' | 'unknown'
  message: string
}

export interface VoiceNoteResult {
  transcript: string
  attachment: AttachmentRef
}

export interface UseVoiceRecorderOptions {
  onResult: (result: VoiceNoteResult) => void
  onError?: (error: VoiceRecorderError) => void
}

// Bare mime types only (no ``;codecs=`` suffix) — the server matches the
// declared Content-Type against an exact allowlist, so a codec parameter
// MediaRecorder.mimeType would otherwise report must never reach the upload.
const MIME_CANDIDATES = ['audio/webm', 'audio/mp4', 'audio/ogg'] as const
const EXT_BY_MIME: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/ogg': 'ogg',
}

function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) ?? null
}

function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    pickMimeType() !== null
  )
}

export function useVoiceRecorder(options: UseVoiceRecorderOptions) {
  const { onResult, onError } = options
  const [status, setStatus] = useState<VoiceRecorderStatus>(() =>
    isSupported() ? 'idle' : 'unsupported'
  )
  const [error, setError] = useState<VoiceRecorderError | null>(null)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const mimeTypeRef = useRef<string>('audio/webm')

  const fail = useCallback(
    (voiceError: VoiceRecorderError) => {
      setError(voiceError)
      setStatus('error')
      onError?.(voiceError)
    },
    [onError]
  )

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    recorderRef.current = null
  }, [])

  const uploadAndTranscribe = useCallback(
    async (blob: Blob) => {
      setStatus('transcribing')
      const ext = EXT_BY_MIME[mimeTypeRef.current] ?? 'webm'
      const baseUrl = getBaseUrl()
      const user = useAuthStore.getState().user
      const businessId = (user as Record<string, unknown> | null)?.default_business_id as
        | string
        | undefined

      try {
        const formData = new FormData()
        formData.append('audio', blob, `voice-note.${ext}`)

        const response = await fetchWithAuthRetry((token) => {
          const headers: Record<string, string> = {}
          if (token) headers['Authorization'] = `Bearer ${token}`
          if (businessId) headers['X-Business-ID'] = businessId
          return fetch(`${baseUrl}/agent/voice-notes`, {
            method: 'POST',
            headers,
            body: formData,
          })
        })

        if (!response.ok) {
          let detail = response.statusText
          try {
            const errBody = await response.json()
            detail = errBody.detail || errBody.message || errBody.error?.message || detail
          } catch {
            // ignore
          }
          if (response.status === 422) {
            fail({ type: 'no-speech', message: 'No speech detected. Please try again.' })
          } else {
            fail({ type: 'network', message: detail })
          }
          return
        }

        const data = (await response.json()) as {
          upload_id: string
          url: string
          mime_type: string
          filename: string
          size_bytes: number
          transcript: string
        }

        onResult({
          transcript: data.transcript,
          attachment: {
            upload_id: data.upload_id,
            url: data.url,
            mime_type: data.mime_type,
            // Named for what it is: the bubble shows an audio attachment by its name.
            filename: 'Voice note',
            size_bytes: data.size_bytes,
          },
        })
        setStatus('idle')
        setError(null)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Could not upload the voice note.'
        fail({ type: 'network', message })
      }
    },
    [fail, onResult]
  )

  const startRecording = useCallback(async () => {
    if (status === 'recording' || status === 'requesting_permission') return
    if (!isSupported()) {
      fail({
        type: 'unsupported',
        message: 'Voice notes are not supported in this browser.',
      })
      return
    }

    setError(null)
    setStatus('requesting_permission')

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      fail({
        type: 'not-allowed',
        message: 'Microphone access denied. Please allow microphone access in your settings.',
      })
      return
    }

    const mimeType = pickMimeType()
    if (!mimeType) {
      stream.getTracks().forEach((track) => track.stop())
      fail({ type: 'unsupported', message: 'No supported audio format for recording.' })
      return
    }

    mimeTypeRef.current = mimeType
    streamRef.current = stream
    chunksRef.current = []

    const recorder = new MediaRecorder(stream, { mimeType })
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data)
    }
    recorderRef.current = recorder
    recorder.start()
    setStatus('recording')
  }, [status, fail])

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || status !== 'recording') return

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current })
      chunksRef.current = []
      cleanupStream()
      if (blob.size === 0) {
        fail({ type: 'no-speech', message: 'No audio recorded. Please try again.' })
        return
      }
      void uploadAndTranscribe(blob)
    }
    recorder.stop()
  }, [status, cleanupStream, fail, uploadAndTranscribe])

  return {
    status,
    isRecording: status === 'recording',
    isSupported: status !== 'unsupported',
    error,
    startRecording,
    stopRecording,
  }
}

export default useVoiceRecorder
